/// <amd-module name="Dashboards/WidgetCommon/WidgetTelemetry" />
import { WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

/** Provides a family of Widget-centered telemetry events. */
export interface IWidgetTelemetryService extends IVssService {
    /**
     * Produces standard click event telemetry for all widgets. Always reports telemetry immediately.
     * @param {WidgetProps} widgetProps
     * @param {string} linkDescription describes what the clicked link points at - should be unique in scope of that widget type and should not include spaces. For example: "CreateWorkItem"
     */
    onWidgetClick(
        widgetProps: WidgetProps,
        options?: Partial<{
            linkDescription: string;
        }>
    ): void;

    /**
     * Produces an event when an arbitrary error is encountered. Use this for tracking of any one-off failures which do not get tracked by widgetHost, or more specialized widget telemetry.
     * For errors which do not get tracked with widgetHost error UI.
     * @param widgetProps
     * @param operationName - Unique, meaningful description of the operation which failed for traceability
     * @param extendedProps - provides additional context about the error. To support detailed investigation.
     */
    onWidgetFailure(widgetProps: WidgetProps, errorMessage: string, operationName: string, extendedProps: { [key: string]: any }): void;

    /**
     * Produces an event when the widget is loaded. All custom properties supplied here will be tracked on Widget Metrics dashboard.
     * @param widgetProps - Properties of the widget
     * @param properties - Widget-specific information about the state of the widget, such as configuration and data shape.
     */
    onWidgetLoaded(widgetProps: WidgetProps, properties: { [key: string]: any }): void;

    /**
     * Produces an event when the widget is loading eg.during settings generation.
     * @param widgetProps - Properties of the widget
     * @param properties - Widget-specific information about the state of the widget, such as configuration and data shape.
     */
    onWidgetLoading(widgetProps: WidgetProps, properties: { [key: string]: any }): void;
}

export class DashboardsTelemetryConstants {
    /**
     * Area for our CI events.
     */
    public static Area: string = "Dashboards";
}

export class WidgetTelemetryService extends VssService implements IWidgetTelemetryService {
    private telemetryService: IVssTelemetryService;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this.telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    public onWidgetClick(
        widgetProps: WidgetProps,
        options: Partial<{
            linkDescription: string;
        }> = {}
    ): void {
        const linkDescription = options.linkDescription ? options.linkDescription : "GenericClick";
        const vssPageService: IVssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData() as IPageData;
        const sessionId = pageData.sessionId;

        // Parse the beginning of the contributionId for continuity with the existing "typeId" centric telemetry
        const contributionId = widgetProps.contributionId;
        const typeId: string = contributionId.startsWith("ms.vss-dashboards-web.")
            ? contributionId.replace("ms.vss-dashboards-web.", "")
            : contributionId;
        const measuredFeatureName = "WidgetClicked";

        const properties: { [key: string]: any } = {
            WidgetId: widgetProps.id,
            WidgetTypeId: typeId, // Deprecated Pattern
            ContributionId: widgetProps.contributionId,
            WidgetPresentationMode: widgetProps.presentationMode,
            Description: linkDescription,
            StartTime: Date.now(),
            SessionId: sessionId
        };

        /**
         * TODO: Ensure that this telemetry is logged immediately, as this would mirror the old telemetry experience. The new Telemetry service does not include the
         * "immediate" property and it is unclear if this is done so automatically - User Story 1392495;
         */
        this.telemetryService.publishEvent(DashboardsTelemetryConstants.Area, measuredFeatureName, properties);
    }

    public onWidgetFailure(widgetProps: WidgetProps, errorMessage: string, operationName: string, properties: { [key: string]: any } = {}): void {
        const measuredFeatureName = "WidgetFailed";
        const extendedProps = {
            WidgetId: widgetProps.id,
            WidgetTypeId: widgetProps.contributionId, // Deprecated Pattern
            ContributionId: widgetProps.contributionId,
            WidgetPresentationMode: widgetProps.presentationMode,
            ErrorMessage: errorMessage,
            // "ErrorId": DashboardsTelemetryCore.extractErrorId(errorMessage), TODO convert dependencies to maintain telemetry. #1472418
            OperationName: operationName,
            ...properties
        };
        this.telemetryService.publishEvent(DashboardsTelemetryConstants.Area, measuredFeatureName, extendedProps);
    }

    public onWidgetLoaded(widgetProps: WidgetProps, properties: { [key: string]: any }): void {
        this.generateAndPublishTelemetry(widgetProps, properties, "WidgetLoaded");
    }

    public onWidgetLoading(widgetProps: WidgetProps, properties: { [key: string]: any }): void {
        this.generateAndPublishTelemetry(widgetProps, properties, "WidgetLoading");
    }

    private generateAndPublishTelemetry(widgetProps: WidgetProps, properties: { [key: string]: any }, measuredFeatureName: string): void {
        const coreProps = {
            WidgetId: widgetProps.id,
            WidgetTypeId: widgetProps.contributionId, // Deprecated Pattern
            ContributionId: widgetProps.contributionId,
            WidgetPresentationMode: widgetProps.presentationMode,
            ParentPageName: widgetProps.parentPageName,
            TeamId: widgetProps.teamContext.id, // Analytics reports have WidgetId as undefined, added TeamId to uniquely identify AX Reports 
            WidgetProperties: {
                Width: widgetProps.size.width,
                Height: widgetProps.size.height,
                Size: `${widgetProps.size.width} * ${widgetProps.size.height}`,
                ...properties // Pack the widget-specific details here. This allows us to ignore stock dashboard supplied fields in modern, downstream reporting.
            }
        };
        this.telemetryService.publishEvent(DashboardsTelemetryConstants.Area, measuredFeatureName, coreProps);
    }
}

Services.add("IWidgetTelemetryService", {
    serviceFactory: WidgetTelemetryService
});

export function onWidgetClick(
    pageContext: IVssPageContext,
    widgetProps: WidgetProps,
    options?: Partial<{
        linkDescription: string;
    }>
): void {
    const widgetTelemetryService = pageContext.getService<IWidgetTelemetryService>("IWidgetTelemetryService");
    widgetTelemetryService.onWidgetClick(widgetProps, options);
}

export function onWidgetFailure(
    pageContext: IVssPageContext,
    widgetProps: WidgetProps,
    errorMessage: string,
    operationName: string,
    extendedProps: { [key: string]: any }
): void {
    const widgetTelemetryService = pageContext.getService<IWidgetTelemetryService>("IWidgetTelemetryService");
    widgetTelemetryService.onWidgetFailure(widgetProps, errorMessage, operationName, extendedProps);
}

export function onWidgetLoaded(pageContext: IVssPageContext, widgetProps: WidgetProps, properties?: { [key: string]: any }): void {
    const widgetTelemetryService = pageContext.getService<IWidgetTelemetryService>("IWidgetTelemetryService");
    widgetTelemetryService.onWidgetLoaded(widgetProps, { ...properties });
}

export function onWidgetLoading(pageContext: IVssPageContext, widgetProps: WidgetProps, properties?: { [key: string]: any }): void {
    const widgetTelemetryService = pageContext.getService<IWidgetTelemetryService>("IWidgetTelemetryService");
    widgetTelemetryService.onWidgetLoading(widgetProps, { ...properties });
}

/**
 * @deprecated - Existing usages should be factored to drop and omit, when safe to do so.
 *
 * Calls a function and records telemetry for the time it takes.
 * This was used as a legacy perf diagnostic in early days of analytics, and isn't used as part of modern widget development patterns.
 *
 * for the function to execute and resolve its returned promise.
 * If the function throws an exception or the promise is rejected a failure event is recorded instead.
 * @param funcName - The name of the argument to asyncFunc
 * @param asyncFunc - The function or a delegate that calls the function to execute and time
 * @param featureName - The name of the feature for which telemetry is being recorded
 * @param extendedProperties - (Optional) Any additional information that should be recorded about the event
 * @returns <IPromise<T>> The promise returned by asyncFunc
 */
export function executeAndTimeAsync<T>(
    pageContext: IVssPageContext,
    featureName: string,
    funcName: string,
    asyncFunc: () => Promise<T>,
    errorStringifier?: (e: any) => string,
    extendedProperties?: { [key: string]: any }
): Promise<T> {
    let promise = null;
    let properties: { [key: string]: any } = {
        Function: funcName,
        StartDate: new Date(),
        StartTime: Date.now()
    };
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

    const publishSuccess = () => {
        properties["ElapsedTimeMs"] = Date.now() - properties["StartTime"];
        properties = { ...properties, ...extendedProperties };

        telemetryService.publishEvent(DashboardsTelemetryConstants.Area, featureName, properties);
    };

    const publishError = (e: any) => {
        properties["ElapsedTimeMs"] = Date.now() - properties["StartTime"];
        const parsedError = errorStringifier ? errorStringifier(e) : e;
        properties["Error"] = parsedError;
        properties = { ...properties, ...extendedProperties };

        telemetryService.publishEvent(DashboardsTelemetryConstants.Area, featureName, properties);
    };

    try {
        // We use the promise returned by .then rather than the promise returned by asyncFunc directly to ensure
        // the execution time calculation isn't delayed by any .thens chained to this function call.
        promise = asyncFunc().then(
            value => {
                publishSuccess();
                return value;
            },
            e => {
                publishError(e);
                throw e; // Throw the error so the promise is rejected
            }
        );
    } catch (e) {
        publishError(e);
        throw e; // Throw the error so it can be handled by the caller
    }

    return promise;
}
